/**
 * Mutations Service — Platform-driven F&O + Sage employee updates.
 *
 * Thin wrapper over the integration-service mutation saga endpoints.
 * Mirrors the backend ``app/mutations/router.py`` + ``app/mutations/schemas.py``.
 *
 * Flow for a caller:
 *   1. ``listMutationTypes()`` → discover supported types + field allow-lists
 *   2. ``submitMutation()``    → 202 Accepted, saga runs in background
 *   3. ``getMutation()`` every 2s until ``overall_status`` is terminal
 *
 * Terminal overall_status values: ``completed`` | ``partial`` | ``failed`` | ``cancelled``.
 * ``partial`` = F&O succeeded + Sage failed (or was skipped with an error).
 */

import { apiClient } from '../apiClient';

// ─────────────────────────────────────────────────────────────────────
// Types — mirror backend schemas
// ─────────────────────────────────────────────────────────────────────

// Backend overall_status values — saga.py::_compute_overall_status
// ("running" until both sides land, then "completed" | "partial" |
// "failed").  Add "cancelled" for defensive UI handling.
export type MutationOverallStatus =
  | 'running'
  | 'completed'
  | 'partial'
  | 'failed'
  | 'cancelled';

// Backend per-side status values — saga.py::_apply_side_result.
// Note: NOT 'success'/'failure'/'running' — the saga writes
// 'in_progress' / 'succeeded' / 'failed' / 'skipped' / 'pending'.
export type MutationSideStatus =
  | 'pending'
  | 'in_progress'
  | 'succeeded'
  | 'skipped'
  | 'failed';

export interface MutationSideError {
  stage?: string;
  error?: string;
  http_status?: number;
  details?: Record<string, unknown>;
  skipped_reason?: string;
  // Lines a side reports on success: what else it changed (notes) and
  // what it tried that did not land (warnings).
  notes?: string[];
  warnings?: string[];
}

export interface MutationResponse {
  mutation_id: string;
  employee_code: string;
  mutation_type: string;
  fields_before: Record<string, unknown> | null;
  fields_after: Record<string, unknown>;
  overall_status: MutationOverallStatus;
  fo_status: MutationSideStatus;
  sage_status: MutationSideStatus;
  fo_error: MutationSideError | null;
  sage_error: MutationSideError | null;
  triggered_by: string | null;
  created_at: string;
  started_at: string | null;
  fo_completed_at: string | null;
  sage_completed_at: string | null;
  completed_at: string | null;
}

export interface CreateMutationRequest {
  mutation_type: string;
  fields_after: Record<string, unknown>;
  /** Date the change takes effect (YYYY-MM-DD). Send the same value as
   *  fields_after.EffectiveDate: the server fills it in from EffectiveDate
   *  when it is missing and refuses a request where the two differ. The
   *  overnight run completes a future-dated change by this stored date. */
  effective_date?: string;
  reason?: string;
  /** Optional client-side snapshot of the PRE-amendment values, keyed by
   *  the same field names in ``fields_after``.  When provided, the
   *  backend uses this as the authoritative ``fields_before`` and only
   *  runs its own ``fetch_before_state`` as a supplement.  Cheaper +
   *  more accurate than the backend re-fetching, and avoids null
   *  ``fields_before`` when Sage auth hiccups. */
  fields_before_snapshot?: Record<string, unknown>;
  /** Provenance tag — defaults server-side to ``'user'``.  Set to
   *  ``'workflow'`` when submitting from the Hub Task Queue panels and
   *  ``'hub_revert'`` for counter-mutations from Drift Review.  Drives
   *  Hub Audit filtering and the diff service's saga-attribution
   *  exclusion of hub-initiated writes. */
  source?: 'user' | 'hub_revert' | 'workflow' | 'bulk_sync';
  /** Per-handler options.  Unrecognised keys are ignored server-side.
   *  Notable values used by hub-driven flows:
   *    * ``hub_task_id`` (string) — the originating ``HubTask.task_id``;
   *      reinstatement handler uses this to stamp Sage's
   *      ``ReferenceNumber`` for cross-system audit search. */
  options?: Record<string, unknown>;
}

export interface MutationTypesResponse {
  mutation_types: Record<string, string[]>;
}

// ─────────────────────────────────────────────────────────────────────
// Terminal / in-flight helpers (pure predicates for the UI to reuse)
// ─────────────────────────────────────────────────────────────────────

export const TERMINAL_OVERALL_STATUSES: MutationOverallStatus[] = [
  'completed',
  'partial',
  'failed',
  'cancelled',
];

export const isTerminal = (s: MutationOverallStatus): boolean =>
  TERMINAL_OVERALL_STATUSES.includes(s);

// ─────────────────────────────────────────────────────────────────────
// Service
// ─────────────────────────────────────────────────────────────────────

class MutationsService {
  /** Discover every registered mutation type + its allow-list of fields. */
  async listMutationTypes(): Promise<MutationTypesResponse> {
    const response = await apiClient.get<MutationTypesResponse>(
      '/api/v1/integration/mutations/types',
    );
    return response.data;
  }

  /** Submit a new mutation.  Returns 202 Accepted with the initial record;
   *  the saga runs in the background — poll via ``getMutation``. */
  async submitMutation(
    employeeCode: string,
    request: CreateMutationRequest,
  ): Promise<MutationResponse> {
    // The backend schema's audit-note field is ``notes`` — every panel
    // here passes ``reason``, which the API silently ignored (Pydantic
    // drops unknown keys), so no submit ever landed its note.  Map it
    // at the single choke point rather than churning every call site.
    const { reason, ...rest } = request;
    const body = { ...rest, ...(reason ? { notes: reason } : {}) };
    const response = await apiClient.post<MutationResponse>(
      `/api/v1/integration/employees/${encodeURIComponent(employeeCode)}/mutations`,
      body,
    );
    return response.data;
  }

  /** Poll one mutation by id.  Caller is responsible for loop + interval. */
  async getMutation(
    employeeCode: string,
    mutationId: string,
  ): Promise<MutationResponse> {
    const response = await apiClient.get<MutationResponse>(
      `/api/v1/integration/employees/${encodeURIComponent(employeeCode)}/mutations/${encodeURIComponent(mutationId)}`,
    );
    return response.data;
  }

  /** List recent mutations for one employee (most-recent first). */
  async listMutations(
    employeeCode: string,
    limit = 50,
  ): Promise<MutationResponse[]> {
    const response = await apiClient.get<MutationResponse[]>(
      `/api/v1/integration/employees/${encodeURIComponent(employeeCode)}/mutations`,
      { params: { limit } },
    );
    return response.data;
  }
}

export const mutationsService = new MutationsService();
